import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Input } from '~/components/atomic';
import { fetchCheckEmail } from '~/api';
import { useDebounce } from '~/hooks';
import { isGmail, validateEmail } from '~/utils';
import { useSignUpStepStore } from '~/store';
import ErrorText from '../Error/ErrorText';
import HelperText from '../Error/HelperText';
import InnerLayout from '../Layout/InnerLayout';

const CHECK_EMAIL_TIME = 500;

const EMAIL_VALIDATE_MSG = 'Please enter a valid email.';
const GOOGLE_VALIDATE_MSG =
  'Please use Social login for Gmail email addresses.';
const USER_ACCOUNT_MSG = 'The account already exists. Please try to login.';

interface CheckEmailProps {
  onNextStep: () => void;
}

export default function CheckEmail({ onNextStep }: CheckEmailProps) {
  const { email, setEmail } = useSignUpStepStore((state) => ({
    email: state.verifyEmail.email,
    setEmail: state.setEmail,
  }));
  const setSubProgress = useSignUpStepStore((state) => state.setSubProgress);
  const [disabled, setDisabled] = useState(true);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setSubProgress(disabled ? 0.5 : 1);
  }, [disabled, setSubProgress]);

  const debouncedEmail = useDebounce(email, CHECK_EMAIL_TIME);

  const checkEmail = useCallback(async () => {
    if (!validateEmail(debouncedEmail)) {
      setError(EMAIL_VALIDATE_MSG);
      setDisabled(true);

      return;
    }

    if (isGmail(debouncedEmail)) {
      setError(GOOGLE_VALIDATE_MSG);
      setDisabled(true);
      return;
    }
    setIsChecking(true);
    try {
      const { data } = await fetchCheckEmail({ email: debouncedEmail });
      //사용 중이면 0, 아니라면 1
      if (!data.used) {
        setError('');
        setDisabled(false);
      } else {
        setError(USER_ACCOUNT_MSG);
        setDisabled(true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsChecking(false);
    }
  }, [debouncedEmail]);

  useEffect(() => {
    if (debouncedEmail === '') {
      setError('');
      setDisabled(true);
      return;
    }

    checkEmail();
  }, [debouncedEmail, checkEmail]);

  const buttonProps = {
    title: 'Next',
    disabled,
    onClick: onNextStep,
  };

  return (
    <InnerLayout buttonProps={buttonProps}>
      <div>
        <Input
          id='SignUpCheckEmail'
          placeholder='Email'
          text={email}
          setText={setEmail}
          deleteAllTextOpt
        />
        {error && <ErrorText error={error} />}
        {isChecking && <HelperText text='Checking...' />}
      </div>
    </InnerLayout>
  );
}
